import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import CommonModal from "../common11/commonModals/CommonModal";
import CommonDatePicker from "../common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../common11/formInputs/CommonTimePicker";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import InputAreaField from "../common11/formInputs/InputAreaField";

// Sample doctor options – replace with API when available
const DOCTOR_OPTIONS = [
  { id: 1, label: "Ajay Oswal", value: "ajay" },
  { id: 2, label: "Vivek Deshmukh", value: "vivek" },
  { id: 3, label: "Dr Tukaram Aute", value: "tukaram" },
  { id: 4, label: "Dr Mahesh Mandora", value: "mahesh" },
];

const ReferPatientModal = ({ isOpen, onClose, patientInfo = {} }) => {
  const name = patientInfo?.name ?? "—";
  const uhid = patientInfo?.uhid ?? "—";
  const age = patientInfo?.age ?? patientInfo?.ageGender ?? "—";
  const gender = patientInfo?.gender ?? "—";

  const methods = useForm({
    defaultValues: {
      referDate: new Date(),
      referTime: "11:27:12",
      referredTo: null,
      referredBy: null,
      remark: "",
    },
  });

  const { reset } = methods;

  const handleSave = () => {
    // TODO: API call to save referral
    onClose();
  };

  const handleReset = () => {
    reset({
      referDate: new Date(),
      referTime: "",
      referredTo: null,
      referredBy: null,
      remark: "",
    });
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Refer Patient"
      size="lg"
      showCloseButton
      disableBackdropClose={true}
      DialogContent={
        <FormProvider {...methods}>
          <div className="space-y-4">
            {/* 1. Basic patient info – same style as Test Compare */}
            <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                    {name && name !== "—" ? (
                      <span className="text-lg font-semibold text-gray-600">
                        {String(name).charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-lg font-semibold text-gray-600">?</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-1 flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm min-w-0">
                  <span className="text-[#217E90] font-semibold truncate">{name}</span>
                  <div className="flex items-baseline gap-3">
                    <span className="text-gray-500 text-xs shrink-0">Age</span>
                    <span className="text-[#217E90] font-semibold">: {age}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-gray-500 text-xs shrink-0">Gender</span>
                    <span className="text-[#217E90] font-semibold">: {gender}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-gray-500 text-xs shrink-0">UHID</span>
                    <span className="text-[#217E90] font-semibold">: {uhid}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Referral listing */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-[#F8FEFF] sticky top-0 z-10">
                    <tr>
                      <th className="border-b border-r px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Refer Date
                      </th>
                      <th className="border-b border-r px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Refer Time
                      </th>
                      <th className="border-b border-r px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Refer To Doc
                      </th>
                      <th className="border-b border-r px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Refer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Empty state – replace with API data when available */}
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500">
                        No referral records yet
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3. Form fields */}
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CommonDatePicker
                  name="referDate"
                  control={methods.control}
                  label="Date"
                  isFloatingLabel
                  fontSize="text-xs"
                />
                <CommonTimePicker
                  name="referTime"
                  control={methods.control}
                  label="Time"
                  isFloatingLabel
                  fontSize="text-xs"
                  placeholder="Time"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SearchDropdown
                  name="referredTo"
                  control={methods.control}
                  label="Referred To"
                  dataArray={DOCTOR_OPTIONS}
                  isSearchable
                  isClearable
                  isFloatingLabel
                  fontSize="text-xs"
                  selectSize="h-8"
                  placeholder="Select doctor..."
                />
                <SearchDropdown
                  name="referredBy"
                  control={methods.control}
                  label="Referred By"
                  dataArray={DOCTOR_OPTIONS}
                  isSearchable
                  isClearable
                  isFloatingLabel
                  fontSize="text-xs"
                  selectSize="h-8"
                  placeholder="Select doctor..."
                />
              </div>
              <InputAreaField
                name="remark"
                control={methods.control}
                label="Remark"
                placeholder="Enter remark..."
                rows={3}
              />
            </div>
          </div>
        </FormProvider>
      }
      actions={[
        {
          onClick: handleReset,
          label: "Reset",
          variant: "danger-outline",
          size: "sm",
          type: "button",
          className: "flex-1 sm:flex-none px-3 md:px-5 text-xs md:text-sm",
        },
        {
          onClick: handleSave,
          label: "Save",
          variant: "primary-filled",
        },
      ]}
    />
  );
};

export default ReferPatientModal;
